import React from 'react';
import PropTypes from 'prop-types';
import keycode from 'keycode';
import { get, without, union, last, findLastIndex, isArray, isFunction } from 'lodash';
import clxBind from 'classnames/bind';
import styles from './styles.module.scss';

const clx = clxBind.bind(styles);
const noop = (a, b) => b;

export default class List extends React.Component {
  static propTypes = {
    options: PropTypes.array.isRequired,
    selected: PropTypes.array,
    labelKey: PropTypes.string.isRequired,
    valueKey: PropTypes.string,
    className: PropTypes.string,
    onSelect: PropTypes.func,
    onKeyDown: PropTypes.func,
    onOptionClick: PropTypes.func,
    onOptionDoubleClick: PropTypes.func,
    onOptionFocus: PropTypes.func,
  };

  static defaultProps = {
    selected: [],
    valueKey: undefined,
    className: undefined,
    onSelect: undefined,
    onKeyDown: undefined,
    onOptionClick: undefined,
    onOptionDoubleClick: undefined,
    onOptionFocus: undefined,
  };

  constructor(props) {
    super(props);

    this.state = {
      focusedIndex: -1,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { options, selected, valueKey, labelKey } = nextProps;

    if (options === this.props.options) {
      return;
    }

    const { focusedIndex } = this.state;
    const key = valueKey || labelKey;
    const focusedValue = get(this.props.options, [focusedIndex, key]);

    if (!selected.includes(focusedValue)) {
      const toFocusValue = last(selected);
      const toFocusIndex = findLastIndex(options, o => o[key] === toFocusValue);

      this.scrollToFocusedOptionOnUpdate = true;
      this.focusOption(toFocusIndex);
    }
  }

  componentDidUpdate() {
    if (this.scrollToFocusedOptionOnUpdate && this.focused && this.menu) {
      this.scrollToFocusedOptionOnUpdate = false;

      const focusedRect = this.focused.getBoundingClientRect();
      const menuRect = this.menu.getBoundingClientRect();

      if (focusedRect.bottom > menuRect.bottom) {
        this.menu.scrollTop =
          this.focused.offsetTop +
          this.focused.clientHeight -
          this.menu.offsetTop -
          this.menu.offsetHeight;
      } else if (focusedRect.top < menuRect.top) {
        this.menu.scrollTop = this.focused.offsetTop - this.menu.offsetTop;
      }
    }
  }

  getRange = endIndex => {
    const { options, labelKey, valueKey } = this.props;
    const key = valueKey || labelKey;

    if (this.startIndex === -1) {
      return options[endIndex][key];
    }

    const start = Math.min(this.startIndex, endIndex);
    const length = Math.abs(this.startIndex - endIndex) + 1;

    return Array.from({ length }, (v, k) => options[start + k][key]);
  };

  getValue = i => {
    const { options, labelKey, valueKey } = this.props;
    const key = valueKey || labelKey;

    return options[i][key];
  };

  startIndex = -1;
  scrollToFocusedOptionOnUpdate = false;

  handleOptionMouseDown = (value, i) => event => {
    if (event.shiftKey && this.startIndex > -1) {
      const range = this.getRange(i);
      this.selectOptions(range);
      this.focusOption(i);
    } else {
      let applySelection;

      if (event.ctrlKey) {
        const isSelected = this.props.selected.includes(value);
        applySelection = isSelected ? (a, b) => without(a, ...b) : union;
      }

      this.selectOptions(value, applySelection);
      this.focusOption(i);

      this.startIndex = i;
    }
  };

  handleOptionClick = (value, i) => event => {
    const { onOptionClick } = this.props;

    event.preventDefault();

    if (isFunction(onOptionClick)) {
      onOptionClick(value, i);
    }
  };

  handleOptionDoubleClick = (value, i) => event => {
    const { onOptionDoubleClick } = this.props;

    event.preventDefault();

    if (isFunction(onOptionDoubleClick)) {
      onOptionDoubleClick(value, i);
    }
  };

  handleOptionFocus = () => {
    if (this.menu) {
      this.menu.focus();
    }
  };

  handleKeyDown = event => {
    const { options, selected, onKeyDown } = this.props;
    const { focusedIndex } = this.state;

    if (isFunction(onKeyDown)) {
      onKeyDown(focusedIndex, selected, event);
    }

    if (event.defaultPrevented) {
      return;
    }

    let next = focusedIndex !== -1 ? focusedIndex : 0;

    switch (keycode(event.keyCode)) {
      case 'up':
        if (options.length === 0) {
          break;
        }

        this.scrollToFocusedOptionOnUpdate = true;
        next = (next <= 0 ? options.length : next) - 1;
        this.moveSelection(next, event.shiftKey);

        break;
      case 'down':
        if (options.length === 0) {
          break;
        }

        this.scrollToFocusedOptionOnUpdate = true;
        next = (next + 1) % options.length;
        this.moveSelection(next, event.shiftKey);

        break;
      case 'a':
        if (options.length === 0) {
          break;
        }

        if (event.ctrlKey) {
          this.selectAll();
        }

        break;
      default:
        return;
    }

    event.preventDefault();
  };

  handleOptionRef = (ref, isFocused) => {
    if (isFocused) {
      this.focused = ref;
    }
  };

  handleMenuRef = ref => {
    this.menu = ref;
  };

  focusOption = i => {
    const { onOptionFocus } = this.props;

    this.setState({ focusedIndex: i });

    if (onOptionFocus) {
      onOptionFocus(i);
    }
  };

  selectOptions = (options, applySelection = noop) => {
    const { onSelect } = this.props;
    const select = isArray(options) ? options : [options];
    const selected = applySelection(this.props.selected, select);

    if (isFunction(onSelect)) {
      onSelect(selected);
    }
  };

  selectAll = () => {
    const { options } = this.props;

    this.startIndex = 0;

    const lastIndex = options.length - 1;
    const range = this.getRange(lastIndex);

    this.focusOption(lastIndex);
    this.selectOptions(range);
  };

  moveSelection = (next, shiftKey) => {
    if (shiftKey && this.startIndex > -1) {
      const range = this.getRange(next);
      this.selectOptions(range);
    } else {
      const nextValue = this.getValue(next);
      this.selectOptions(nextValue);
      this.startIndex = next;
    }

    this.focusOption(next);
  };

  renderOption = (option, i) => {
    const { labelKey, valueKey, selected } = this.props;
    const { focusedIndex } = this.state;
    const key = valueKey || labelKey;
    const value = option[key];
    const label = option[labelKey];

    const isSelected = selected.includes(value);
    const isFocused = focusedIndex === i;

    const optionClasses = clx('option', {
      selected: isSelected,
    });

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <div
        key={`option-${value}`}
        ref={ref => this.handleOptionRef(ref, isFocused)}
        onClick={this.handleOptionClick(value, i)}
        onDoubleClick={this.handleOptionDoubleClick(value, i)}
        onMouseDown={this.handleOptionMouseDown(value, i)}
        onFocus={this.handleOptionFocus}
        role="option"
        aria-selected={isSelected}
        tabIndex={-1}
        className={optionClasses}
      >
        {label}
      </div>
    );
  };

  render() {
    const { options, className } = this.props;
    const listClasses = clx('list', className);

    return (
      <div
        ref={this.handleMenuRef}
        className={listClasses}
        onKeyDown={this.handleKeyDown}
        tabIndex={0}
        role="menu"
      >
        {options.map(this.renderOption)}
      </div>
    );
  }
}
